import { OrgFieldConfig } from "@/api/org_field_config.tsx"
import { ClickToCopyRemotePromoIdsDisplay } from "@/components/click_to_copy_remote_promo_id.tsx"
import { ColumnHeader } from "@/deduction_table/column_header.tsx"
import { cellDateFormatter } from "@/deduction_table/date_column.tsx"
import { exactCurrencyFormatter } from "@/utils"
import { defaultFilter } from "@/utils/filters.ts"

export interface OrgColumnConfig<T> {
  tableContext: string
  getValue: (row: T, fieldName: string) => unknown
  getOrgId: (row: T) => string
  /**
   * Fields this table already renders as a static column. An org column carries the field name as
   * its tanstack `id`, so building one for a field that already has a static column produces two
   * columns with the same id. Defaults to `retailer_name` (static `retailerColumn` everywhere).
   */
  staticFieldNames?: string[]
}

const DEFAULT_STATIC_FIELD_NAMES = ["retailer_name"]

export function createOrgColumns<T>(
  orgId: string,
  fieldConfigs: OrgFieldConfig[],
  config: OrgColumnConfig<T>
) {
  const staticFieldNames = config.staticFieldNames ?? DEFAULT_STATIC_FIELD_NAMES
  return fieldConfigs
    .filter(fieldConfig =>
      fieldConfig.is_active &&
      fieldConfig.table_contexts.includes(config.tableContext) &&
      !staticFieldNames.includes(fieldConfig.field_name)
    )
    .sort((a, b) => a.display_order - b.display_order)
    .map(fieldConfig => createOrgColumn(fieldConfig, config))
}

export function formatOrgAdminFeeValue(value: unknown): string {
  if (value === null || value === undefined || value === "") {
    return ""
  }
  const numberValue = typeof value === "number" ? value : Number(String(value).replace(/[$,]/g, ""))
  return Number.isFinite(numberValue) ? exactCurrencyFormatter(numberValue) : String(value)
}

function createOrgColumn<T>(
  fieldConfig: OrgFieldConfig, 
  config: OrgColumnConfig<T>
) {
  const listHeaderTitle = fieldConfig.field_name === "admin_fee" ? "Fee" : fieldConfig.display_name
  return {
    id: fieldConfig.field_name,
    accessorFn: (row: T) => config.getValue(row, fieldConfig.field_name),
    meta: fieldConfig.field_name === "admin_fee" ? { columnPickerLabel: "Fee" } : undefined,
    header: ({ column }: { column: any }) => <ColumnHeader column={column} title={listHeaderTitle} />,
    enableHiding: true,
    cell: ({ row, getValue }: { row: any; getValue: () => any }) => {
      const value = getValue() ?? ""
      const stringValue = String(value)
      
      if (fieldConfig.field_type === "date") {
        return cellDateFormatter({ getValue: () => stringValue })
      }

      if (fieldConfig.field_name === "admin_fee") {
        return <span>{formatOrgAdminFeeValue(value)}</span>
      }

      if (fieldConfig.field_name === "promotion_remote_ids" && stringValue) {
        return <ClickToCopyRemotePromoIdsDisplay value={stringValue} />
      }

      return <span>{stringValue}</span>
    },
    // Pass the filter function directly: string keys only work when registered in the table's
    // `filterFns`, and "defaultFilter" never was — TanStack silently dropped the filter, so
    // header filters on org columns (e.g. Is Trade) did nothing.
    filterFn: fieldConfig.field_type === "date" ? ("dateFilter" as any) : defaultFilter,
  }
}

if (import.meta.vitest) {
  const { describe, expect, it } = import.meta.vitest

  describe("createOrgColumns filterFn", () => {
    function make_config(field_name: string, field_type: string): OrgFieldConfig {
      return {
        id: "1",
        org_id: "org",
        field_name,
        field_type,
        display_name: field_name,
        display_order: 0,
        is_active: true,
        table_contexts: ["accounting_table"],
        created_at: "",
        updated_at: "",
      } as OrgFieldConfig
    }

    const config: OrgColumnConfig<Record<string, unknown>> = {
      tableContext: "accounting_table",
      getValue: (row, fieldName) => row[fieldName],
      getOrgId: () => "org",
    }

    it("uses a real filter function for non-date fields (string keys are silently dropped when unregistered)", () => {
      const [column] = createOrgColumns("org", [make_config("is_trade", "text")], config)
      expect(typeof (column as any).filterFn).toBe("function")

      // The filter actually filters: an is_trade column with value "true" matches "true", not "false".
      const row = { getValue: () => "true" }
      expect((column as any).filterFn(row, "is_trade", "true")).toBe(true)
      expect((column as any).filterFn(row, "is_trade", "false")).toBe(false)
    })

    it("keeps the registered dateFilter key for date fields", () => {
      const [column] = createOrgColumns("org", [make_config("posting_date", "date")], config)
      expect((column as any).filterFn).toBe("dateFilter")
    })
  })

  describe("formatOrgAdminFeeValue", () => {
    it("formats numeric admin fees as dollars", () => {
      expect(formatOrgAdminFeeValue(2.5)).toBe("$2.50")
      expect(formatOrgAdminFeeValue("1,234.56")).toBe("$1,234.56")
    })

    it("preserves blank and unparsable values", () => {
      expect(formatOrgAdminFeeValue("")).toBe("")
      expect(formatOrgAdminFeeValue("pending")).toBe("pending")
    })
  })
}
